import {useMutation} from "@tanstack/react-query"
import {SecondaryButton} from "components/buttons"
import {handleError, toastSuccess} from "helpers"
import {useModel} from "hooks"
import {attendanceService} from "services"
import {AttendanceStatus} from "types/attendance"
import {YesNo} from "types/common"
import styles from "./IrregularAttendanceBanner.module.css"

type Props = {
    attendanceId: number
    status: AttendanceStatus
    isVerified?: YesNo
    hasCheckedOut: boolean
    disabled?: boolean
    onConfirmed: () => void | Promise<unknown>
}

// Shown to staff for an open attendance flagged irregular (location lost).
// Confirming returns it to in_class and clears the notification status.
export const IrregularAttendanceBanner = ({
    attendanceId,
    status,
    isVerified,
    hasCheckedOut,
    disabled,
    onConfirmed
}: Props) => {
    const model = useModel()

    const confirmMutation = useMutation(
        () =>
            attendanceService.updateDailyAttendance({
                attendanceId,
                status: AttendanceStatus.InClass,
                notificationStatus: null,
                isVerified: isVerified === YesNo.Yes
            }),
        {
            onSuccess: async () => {
                await onConfirmed()
                toastSuccess("Confirmed")
            },
            onError: (err) => {
                handleError(err, true)
            }
        }
    )

    if (status !== AttendanceStatus.Irregular || !model.isStaffOrAdmin() || hasCheckedOut) {
        return null
    }

    return (
        <div className={styles.irregularBanner}>
            <span>
                We lost this student&apos;s location. Confirm the student is still around, or adjust the attendance.
            </span>
            <SecondaryButton
                disabled={disabled}
                loading={confirmMutation.isLoading}
                onClick={() => confirmMutation.mutate()}
                title="Confirm student is still around"
            />
        </div>
    )
}
